//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useContext } from 'react';
import { Grid, makeStyles } from '@material-ui/core';
import { useParams, useHistory } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { AppContext, LayoutContext } from '../common/contexts';
import { PATH_SUB_PAGE_FAQ, PATH_SUB_PAGE_TERMS, PATH_SUB_PAGE_PRIVACY, PATH_HOME, PATH_ADMIN_CONSOLE } from '../system/constants';
import { BASE_URL, HOMEPAGE_ABOUT, HOMEPAGE_CONTACT } from '../common/constants';
import { setClassNameInDefns } from '../common/definitionHelper';
import JobSeekerRegistrations from './subComponents/JobSeekerRegistrations';
import EmployerRegistrations from './subComponents/EmployerRegistrations';
// import AccountDeletions from './subComponents/AccountDeletions';
import SurveyResponses from './subComponents/SurveyResponses';
import SubscriptionCancellations from './subComponents/SubscriptionCancellations';
import PreLaunchPage from './subComponents/PreLaunchPage';
import ContactUs from './subComponents/ContactUs';
import AdvertisementRequests from './subComponents/AdvertisementRequests';
import ActivityStats from './subComponents/ActivityStats';
import ActivityStatsProfile from './subComponents/ActivityStatsProfile';

import AdminNavigationButton from './subComponents/AdminNavigationButton';

import {
    ADMIN_JOB_SEEKER_REGISTRATIONS,
    ADMIN_EMPLOYER_REGISTRATIONS,
    ADMIN_ADVERTISEMENT_REQUESTS,
    ADMIN_ACCOUNT_DELETIONS,
    ADMIN_SURVEY_RESPONSES,
    ADMIN_SUBSCRIPTION_CANCELLATIONS,
    ADMIN_GOOGLE_ANALYTICS,
    ADMIN_PRE_LAUNCH_PAGE,
    ADMIN_CONTACT_US,
    ADMIN_ACTIVITY_STATS,
    ADMIN_ID_JOB_SEEKER,
    ADMIN_ID_EMPLOYER_REGISTRATIONS,
    ADMIN_ID_ACCOUNT_DELETIONS,
    ADMIN_ID_ADVERTISEMENT_REQUESTS,
    ADMIN_ID_SURVEY_RESPONSES,
    ADMIN_ID_SUBSCRIPTION_CANCELLATIONS,
    ADMIN_ID_GOOGLE_ANALYTICS,
    ADMIN_ID_PRE_LAUNCH,
    ADMIN_ID_CONTACT_US,
    ADMIN_ID_ACTIVITY_STATS,
} from '../common/constants';
import JobSeekerRegistrationsDetails from './subComponents/JobSeekerRegistrationsDetails';
import { loadTotalCounts, loadTodaysCount } from '../common/services/adminServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
// import AfitdTypography from '../../common/elemental/AfitdTypography';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        root: {},
        sidebarContainer: {
            display: 'flex',
            justifyContent: 'center',
            background: 'white',
            height: '100%',
            overflowY: 'auto',
        },

        sidebarNavItem: { fontSize: 15, color: '#9B9E9C' },
        sidebarItem: { marginTop: 66, maxWidth: 225, marginRight: 32 },
    }));
};
const handleClickButton = (history, path) => {
    history.push(`${BASE_URL}${PATH_ADMIN_CONSOLE}/${path}`);
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, history, admin } = params;

    const defns = {
        sidebarContainer: { container: true },
        sidebarItem: { item: true, xl: 2, lg: 2, md: 2, sm: 2, xs: 2 },

        faqRoute: {
            onClick: handleClickButton.bind(null, history, PATH_SUB_PAGE_FAQ),
        },
        privacyRoute: {
            onClick: handleClickButton.bind(null, history, PATH_SUB_PAGE_PRIVACY),
        },
        termsRoute: {
            onClick: handleClickButton.bind(null, history, PATH_SUB_PAGE_TERMS),
        },
        aboutUsRoute: {
            onClick: handleClickButton.bind(null, history, `${PATH_HOME}/${HOMEPAGE_ABOUT}`),
        },
        contactUsRoute: {
            onClick: handleClickButton.bind(null, history, `${PATH_HOME}/${HOMEPAGE_CONTACT}`),
        },
        jobSeekerRegistrationsButton: {
            label: ADMIN_JOB_SEEKER_REGISTRATIONS,
            selected: admin === ADMIN_JOB_SEEKER_REGISTRATIONS,
            onClick: handleClickButton.bind(null, history, ADMIN_ID_JOB_SEEKER),
        },
        employerRegistrationsButton: {
            label: ADMIN_EMPLOYER_REGISTRATIONS,
            selected: admin === ADMIN_EMPLOYER_REGISTRATIONS,
            onClick: handleClickButton.bind(null, history, ADMIN_ID_EMPLOYER_REGISTRATIONS),
            caption: 2,
            isCaption: true,
        },
        accountDeletionButton: {
            label: ADMIN_ACCOUNT_DELETIONS,
            selected: admin === ADMIN_ACCOUNT_DELETIONS,
            onClick: handleClickButton.bind(null, history, ADMIN_ID_ACCOUNT_DELETIONS),
        },
        advertisementRequestsButton: {
            label: ADMIN_ADVERTISEMENT_REQUESTS,
            selected: admin === ADMIN_ADVERTISEMENT_REQUESTS,
            onClick: handleClickButton.bind(null, history, ADMIN_ID_ADVERTISEMENT_REQUESTS),
        },
        surveyResponsesButton: {
            label: ADMIN_SURVEY_RESPONSES,
            selected: admin === ADMIN_SURVEY_RESPONSES,
            onClick: handleClickButton.bind(null, history, ADMIN_ID_SURVEY_RESPONSES),
            caption: 1,
            isCaption: true,
        },
        subscriptionCancellationsButton: {
            label: ADMIN_SUBSCRIPTION_CANCELLATIONS,
            selected: admin === ADMIN_SUBSCRIPTION_CANCELLATIONS,
            onClick: handleClickButton.bind(null, history, ADMIN_ID_SUBSCRIPTION_CANCELLATIONS),
        },
        googleAnalyticsButton: {
            label: ADMIN_GOOGLE_ANALYTICS,
            selected: admin === ADMIN_GOOGLE_ANALYTICS,
            onClick: handleClickButton.bind(null, history, ADMIN_ID_GOOGLE_ANALYTICS),
        },
        prelaunchButton: {
            label: ADMIN_PRE_LAUNCH_PAGE,
            selected: admin === ADMIN_PRE_LAUNCH_PAGE,
            onClick: handleClickButton.bind(null, history, ADMIN_ID_PRE_LAUNCH),
        },
        reportedThreadsButton: {
            label: ADMIN_CONTACT_US,
            selected: admin === ADMIN_CONTACT_US,
            onClick: handleClickButton.bind(null, history, ADMIN_ID_CONTACT_US),
        },
        activityStatsButton: {
            label: ADMIN_ACTIVITY_STATS,
            selected: admin === ADMIN_ACTIVITY_STATS,
            onClick: handleClickButton.bind(null, history, ADMIN_ID_ACTIVITY_STATS),
        },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const getLayoutComponent = adminPage => {
    switch (adminPage) {
        case '1':
            return <JobSeekerRegistrations />;
        case 'ralph':
            return <JobSeekerRegistrationsDetails />;
        case '2':
            return <EmployerRegistrations />;
        // case '3':
        //     return <AccountDeletions />;
        case '4':
            return <AdvertisementRequests />;
        case '5':
            return <SurveyResponses />;
        case '6':
            return <SubscriptionCancellations />;
        case '8':
            return <PreLaunchPage />;
        case '9':
            return <ContactUs />;
        case '10':
            return <ActivityStats />;
        case 'profile':
            return <ActivityStatsProfile />;
        default:
            return <JobSeekerRegistrations />;
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const AdminConsoleLayout = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const { pageId = ADMIN_JOB_SEEKER_REGISTRATIONS, subPageId = null } = useParams();
    const history = useHistory();
    const appContext = useContext(AppContext);
    // const { currentUser, userId, profileData, currentEmployer, employerId, lovs } = appContext;

    const isSuperUser = _get(appContext, 'currentUser.superUser');

    // const layoutContext = useContext(LayoutContext);
    // const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;
    // const layoutContext = useContext(LayoutContext);
    // const { scrollToPageTop } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { propExample } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [OpenFaqDropdown, setOpenFaqDropdown] = useState(false);
    const [adminPage, setAdminPage] = useState(pageId);
    const [admin, setAdmin] = useState(pageId);
    const [totalCounts, setTotalCounts] = useState(null);
    const [todayCount, setTodayCount] = React.useState(null);
    // console.log(admin);
    // console.log(ADMIN_JOB_SEEKER_REGISTRATIONS);
    // console.log(admin === ADMIN_JOB_SEEKER_REGISTRATIONS, 'end');
    // const [isDirty, setIsDirty] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        const init = async () => {
            const counts = await loadTotalCounts();
            setTotalCounts(counts);
        };
        const init2 = async () => {
            const todayCount = await loadTodaysCount();
            setTodayCount(todayCount);
        };

        init();
        init2();
    }, []);

    useEffect(() => {
        switch (pageId) {
            case ADMIN_ID_JOB_SEEKER:
                setAdmin(ADMIN_JOB_SEEKER_REGISTRATIONS);
                break;
            case ADMIN_ID_EMPLOYER_REGISTRATIONS:
                setAdmin(ADMIN_EMPLOYER_REGISTRATIONS);
                break;
            case ADMIN_ID_ACCOUNT_DELETIONS:
                setAdmin(ADMIN_ACCOUNT_DELETIONS);
                break;
            case ADMIN_ID_ADVERTISEMENT_REQUESTS:
                setAdmin(ADMIN_ADVERTISEMENT_REQUESTS);
                break;
            case ADMIN_ID_SURVEY_RESPONSES:
                setAdmin(ADMIN_SURVEY_RESPONSES);
                break;
            case ADMIN_ID_SUBSCRIPTION_CANCELLATIONS:
                setAdmin(ADMIN_SUBSCRIPTION_CANCELLATIONS);
                break;
            case ADMIN_ID_GOOGLE_ANALYTICS:
                setAdmin(ADMIN_GOOGLE_ANALYTICS);
                break;
            case ADMIN_ID_PRE_LAUNCH:
                setAdmin(ADMIN_PRE_LAUNCH_PAGE);
                break;
            case ADMIN_ID_CONTACT_US:
                setAdmin(ADMIN_CONTACT_US);
                break;
            case ADMIN_ID_ACTIVITY_STATS:
                setAdmin(ADMIN_ACTIVITY_STATS);
                break;

            default:
                setAdmin(ADMIN_JOB_SEEKER_REGISTRATIONS);
        }
    }, [pageId, admin]);

    useEffect(() => {
        switch (pageId) {
            case '1':
                setAdminPage('1');
                break;
            case '2':
                setAdminPage('2');
                break;
            case '3':
                setAdminPage('3');
                break;
            case '4':
                setAdminPage('4');
                break;
            case '5':
                setAdminPage('5');
                break;
            case '6':
                setAdminPage('6');
                break;
            case '8':
                setAdminPage('8');
                break;
            case '9':
                setAdminPage('9');
                break;
            case '10':
                setAdminPage('10');
                break;
            default:
                setAdminPage('1');
        }
        if (subPageId) {
            switch (subPageId) {
                case 'ralph':
                    setAdminPage('ralph');
                    break;
                case 'profile':
                    setAdminPage('profile');
                    break;

                default:
                    setAdminPage('1');
            }
        }
    }, [pageId, subPageId]);

    // useEffect(() => {
    //     scrollToPageTop();
    // }, [scrollToPageTop]);
    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, history, OpenFaqDropdown, setOpenFaqDropdown, admin });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return isSuperUser ? (
        <LayoutContext.Provider value={{ totalCounts, todayCount }}>
            <Grid {...defns.sidebarContainer}>
                <Grid {...defns.sidebarItem}>
                    <div {...defns.sidebarNavItem}>
                        <AdminNavigationButton {...defns.jobSeekerRegistrationsButton} />
                    </div>
                    <div {...defns.sidebarNavItem}>
                        <AdminNavigationButton {...defns.employerRegistrationsButton} />
                    </div>
                    {/* <div {...defns.sidebarNavItem}>
                    <AdminNavigationButton {...defns.accountDeletionButton} />
                </div> */}
                    <div {...defns.sidebarNavItem}>
                        <AdminNavigationButton {...defns.advertisementRequestsButton} />
                    </div>
                    <div {...defns.sidebarNavItem}>
                        <AdminNavigationButton {...defns.surveyResponsesButton} />
                    </div>
                    <div {...defns.sidebarNavItem}>
                        <AdminNavigationButton {...defns.subscriptionCancellationsButton} />
                    </div>
                    <div {...defns.sidebarNavItem}>
                        <AdminNavigationButton {...defns.googleAnalyticsButton} />
                    </div>
                    <div {...defns.sidebarNavItem}>
                        <AdminNavigationButton {...defns.prelaunchButton} />
                    </div>
                    <div {...defns.sidebarNavItem}>
                        <AdminNavigationButton {...defns.reportedThreadsButton} />
                    </div>
                    <div {...defns.sidebarNavItem}>
                        <AdminNavigationButton {...defns.activityStatsButton} />
                    </div>
                </Grid>
                {/* content section */}
                {getLayoutComponent(adminPage)}
            </Grid>
        </LayoutContext.Provider>
    ) : (
        <div>Access denied</div>
    );
};

export default AdminConsoleLayout;
